<script setup lang="ts">
// TODO: AF-694: Refactor the entire table
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useResizeObserver, useWindowSize } from '@vueuse/core';
import type { CellUniqueIdenfier } from '../../../types';
import type { PageState } from 'primevue/paginator';
import { useDownloadButton } from '@ShelterModule';
import useDataTable from '../../../composables/useDataTable';
import useDataTableFilters from '../../../composables/useDataTableFilters';
import { DATA_TABLE_COLUMNS_ORDER } from '../../../constants';
import { DATA_TABLE_COMPONENTS, FORMAT_COMPONENTS } from '../../../enums';
import AActionLoginAs from '../../atoms/AActionLoginAs/AActionLoginAs.vue';
import AAddressCell from '../../atoms/AAddressCell/AAddressCell.vue';
import ABackgroundCell from '../../atoms/ABackgroundCell/ABackgroundCell.vue';
import ACaseStatusLocation from '../../atoms/ACaseStatusLocation/ACaseStatusLocation.vue';
import AComposedLink from '../../atoms/AComposedLink/AComposedLink.vue';
import AConjoinedCell from '../../atoms/AConjoinedCell/AConjoinedCell.vue';
import ADefaultCell from '../../atoms/ADefaultCell/ADefaultCell.vue';
import ADuplicatedPersonCell from '../../atoms/ADuplicatedPersonCell/ADuplicatedPersonCell.vue';
import AEmptyState from '../../atoms/AEmptyState/AEmptyState.vue';
import AFormatedCell from '../../atoms/AFormatedCell/AFormatedCell.vue';
import AHiddenCell from '../../atoms/AHiddenCell/AHiddenCell.vue';
import AHoldLimitsCell from '../../atoms/AHoldLimitsCell/AHoldLimitsCell.vue';
import AImageCell from '../../atoms/AImageCell/AImageCell.vue';
import AInlineGroupCell from '../../atoms/AInlineGroupCell/AInlineGroupCell.vue';
import AInputNumber from '../../atoms/AInputNumber/AInputNumber.vue';
import AKennelModalCell from '../../atoms/AKennelModalCell/AKennelModalCell.vue';
import ALinkCell from '../../atoms/ALinkCell/ALinkCell.vue';
import AListCountCell from '../../atoms/AListCountCell/AListCountCell.vue';
import ALoadingState from '../../atoms/ALoadingState/ALoadingState.vue';
import AMoneyCell from '../../atoms/AMoneyCell/AMoneyCell.vue';
import AOpenModalCell from '../../atoms/AOpenModalCell/AOpenModalCell.vue';
import APasswordExpiryCell from '../../atoms/APasswordExpiryCell/APasswordExpiryCell.vue';
import AReceiptCell from '../../atoms/AReceiptCell/AReceiptCell.vue';
import AShelterStatusCell from '../../atoms/AShelterStatusCell/AShelterStatusCell.vue';
import AStaffEmailVerificationCell from '../../atoms/AStaffEmailVerificationCell/AStaffEmailVerificationCell.vue';
import AStaffPhoneVerificationCell from '../../atoms/AStaffPhoneVerificationCell/AStaffPhoneVerificationCell.vue';
import ATagCell from '../../atoms/ATagCell/ATagCell.vue';
import ATextArea from '../../atoms/ATextArea/ATextArea.vue';
import MActionVouchers from '../../molecules/MActionVouchers/MActionVouchers.vue';
import AImageLink from '../../molecules/MImageLink/MImageLink.vue';

const emits = defineEmits<{
  onLoading: [state: boolean];
  onParamsUpdate: [];
  onFieldUpdate: [newValue: string, field: any, fieldName: string];
}>();

const props = withDefaults(
  defineProps<{
    actionsColumnPosition?: number;
    customEmptyStateHeight?: string;
    customHeight?: string;
    customLoadingHeight?: string;
    hasActions?: boolean;
    hasAsideColumn?: boolean;
    hasFrozenActions?: boolean;
    hasPaginator?: boolean;
    hasSelector?: boolean;
    noPreDefinedFrozenColumns?: boolean;
    tunnelProps?: any;
    tablePageHeader?: any;
    instanceComposable?: any;
    loading?: boolean;
    pageLinkSize?: number;
    sortOrder?: 'asc' | 'desc';
  }>(),
  {
    hasPaginator: true,
    hasSelector: true,
    pageLinkSize: 5,
    sortOrder: 'desc',
  }
);

const dataTableInstance = props.instanceComposable ? props.instanceComposable() : useDataTable();
const filtersInstance = props.instanceComposable ? props.instanceComposable() : useDataTableFilters();

const { activeColumns, page, size } = filtersInstance;
// NOTE: We need to get frozenColumns and visibleFields from instanceComposable
const { columns, rows, selectedRows, totalRecords } = dataTableInstance;
const { FIELD_CLASS, frozenColumns, frozenFields, getFrozenColumnWidth, getFrozenState } = useDataTable();
const { height: windowHeight, width } = useWindowSize({ type: 'visual' });
const { tableHeaders } = useDownloadButton();

const {
  ADDRESS,
  BACKGROUND_CELL,
  CASE_STATUS_LOCATION,
  COMPOSED_LINK,
  CONJOINED,
  DROPDOWN,
  DROPDOWN_MODAL,
  DUPLICATED_PERSON,
  FORMAT,
  HIDDEN_CELL,
  HOLD_STATUS_V2,
  IMAGE,
  IMAGE_LINK,
  INLINE_GROUP,
  INPUT_NUMBER,
  KENNEL_MANAGER_CELL,
  LINK,
  LIST_COUNT,
  LOGIN_AS,
  MONEY,
  MULTISELECT,
  OPEN_MODAL_CELL,
  PASSWORD_EXPIRY,
  RECEIPT,
  SHELTER_STATUS,
  STAFF_EMAIL_VERIFICATION,
  STAFF_PHONE_VERIFICATION,
  TAG,
  TEXT_AREA,
  VOUCHERS,
} = DATA_TABLE_COMPONENTS;
const HARD_CODED_FIELDS = ['actionsColumn', 'asideColumn', 'rowSelector', 'tunnel'];

const headerHeight = ref();
const cellOnEditMode = ref();
const clickOutsideTarget = ref(null);
const gridContainer = ref();
const hasOverflow = ref();
const selectAll = ref();
const scrollPosition = ref(0);

const visibleHeaders = computed(() => {
  const visibleSet = new Set([...HARD_CODED_FIELDS, ...activeColumns.value]);
  return headers.value.filter((header) => visibleSet.has(header.field));
});

const columnsGridSize = computed(() => `repeat(${visibleHeaders.value.length}, auto)`);

const classes = computed(() => [
  'grid-container items-center overflow-auto rounded-md',
  {
    'blur-[1px]': props.loading,
    'min-h-9': windowHeight.value < 822 && rows.value.length === 0,
    'min-h-28': windowHeight.value < 822 && rows.value.length === 1,
    'min-h-[168px]': windowHeight.value < 822 && rows.value.length === 2,
    'min-h-[226px]': windowHeight.value < 822 && rows.value.length === 3,
    'min-h-64': windowHeight.value < 822 && rows.value.length >= 4,
    '!overflow-hidden rounded-b-none': !rows.value.length,
  },
  props.instanceComposable ? 'default' : size.value,
]);

const headers = computed(() => {
  if (!columns.value?.length) return [];

  const result = [...columns.value];

  const { hasSelector, hasAsideColumn, hasActions, tunnelProps } = props;

  if (hasSelector) {
    const rowSelector = {
      header: 'rowSelector',
      field: 'rowSelector',
      settings: undefined,
    };

    result.unshift(rowSelector);
  }

  if (hasAsideColumn) {
    const asideColumn = {
      header: 'asideColumn',
      field: 'asideColumn',
      settings: undefined,
    };

    if (hasSelector) result.splice(1, 0, asideColumn);
    else result.unshift(asideColumn);
  }

  if (tunnelProps) {
    const tunnel = {
      header: tunnelProps?.header || '',
      field: 'tunnel',
      settings: undefined,
    };

    result.push(tunnel);
  }

  if (hasActions) {
    const asideColumn = {
      header: 'actionsColumn',
      field: 'actionsColumn',
      settings: undefined,
    };

    const actionHasPosition = props.actionsColumnPosition;
    if (actionHasPosition) result.splice(props.actionsColumnPosition - 1, 0, asideColumn);
    else result.push(asideColumn);
  }

  return result;
});

const scrollHeight = computed(() => {
  const topNavHeight = 48;
  const header = headerHeight.value || 138;
  const bodyPadding = 16;
  const paginator = 45;
  const bottomPaddingCut = 0;

  const elementsHeight = topNavHeight + header + bodyPadding + paginator + bottomPaddingCut;

  const calcHeight = windowHeight.value - elementsHeight;

  return `${calcHeight}px`;
});

const tableHeader = computed(() => props.tablePageHeader);

useResizeObserver(gridContainer, () => onGridContainerResize());
useResizeObserver(tableHeader, () => getHeaderHeight());

watch(selectedRows, () => onSelectAllRows());
watch([rows, page, width], () => recalculateFrozenColumns());
watch(activeColumns, () => updateTableHeaders());
watch(columns, () => setTableHeaders());

onMounted(() => onDataTableMount());

// HELPERS
// NOTE :: Recalculate frozen column widths after the rows change (e.g. pagination) so the columns fit
// the new content. setFrozenColumns() always assigns a fresh array, which re-triggers the
// measurement in useDataTable; that measurement reads each column at its natural size, so the
// widths do not need to be released here first.
function recalculateFrozenColumns() {
  nextTick(() => setFrozenColumns());
}

function updateTableHeaders() {
  nextTick(() => {
    onToggleColumns();
    setTableHeaders();
  });
}

function onDataTableMount() {
  getHeaderHeight();
  setTableHeaders();
}

function getHeaderHeight() {
  if (typeof tableHeader.value === 'undefined') return;
  if (!tableHeader.value) {
    nextTick(() => getHeaderHeight());
    return;
  }
  headerHeight.value = props.tablePageHeader.getBoundingClientRect().height + 16;
}

function setTableHeaders() {
  if (!headers.value.length) return;
  const DATE_RENDER_TYPES: string[] = [FORMAT_COMPONENTS.DATE_TIME_TO_DATE, FORMAT_COMPONENTS.NUMBER_TO_DATE];

  tableHeaders.value = headers.value.map((header: any) => ({
    label: header.header,
    value: header.field,
    isDate: DATE_RENDER_TYPES.includes(header.settings?.render?.type),
  }));
}

function setFrozenColumns() {
  const gridNotReady = !gridContainer.value;
  if (gridNotReady) return resetFrozenColumns();

  const overflow = gridContainer.value.scrollWidth - gridContainer.value.clientWidth > 1;
  if (hasOverflow.value !== overflow) hasOverflow.value = overflow;
  if (!hasOverflow.value) return resetFrozenColumns();

  const staticAndDynamicColumns = [
    ...frozenFields.value,
    ...(props.noPreDefinedFrozenColumns ? [] : DATA_TABLE_COLUMNS_ORDER),
  ];
  const uniqueFrozenColumns = [...new Set(staticAndDynamicColumns)];

  const rowColumn = props.hasSelector ? 'rowSelector' : '';
  const asideColumn = props.hasAsideColumn ? 'asideColumn' : '';

  const activeHeaders = visibleHeaders.value.map(({ field }) => field);
  const activeFrozenColumns = [rowColumn, asideColumn, ...uniqueFrozenColumns].filter((column) =>
    activeHeaders.includes(column)
  );

  frozenColumns.value = width.value > 1024 ? activeFrozenColumns : [];
}

function resetFrozenColumns() {
  frozenColumns.value = [];
}

function resetSelectAll() {
  selectAll.value = false;
}

function componentSwitcher(component?: DATA_TABLE_COMPONENTS) {
  if (!component) return ADefaultCell;

  //prettier-ignore
  switch (component) {
    case ADDRESS: return AAddressCell;
    case BACKGROUND_CELL: return ABackgroundCell
    case CASE_STATUS_LOCATION: return ACaseStatusLocation;
    case COMPOSED_LINK: return AComposedLink;
    case CONJOINED: return AConjoinedCell;
    case DUPLICATED_PERSON: return ADuplicatedPersonCell;
    case FORMAT: return AFormatedCell;
    case HIDDEN_CELL: return AHiddenCell;
    case HOLD_STATUS_V2: return AHoldLimitsCell;
    case IMAGE: return AImageCell;
    case IMAGE_LINK: return AImageLink;
    case INLINE_GROUP: return AInlineGroupCell;
    case INPUT_NUMBER: return AInputNumber;
    case KENNEL_MANAGER_CELL: return AKennelModalCell;
    case LINK: return ALinkCell;
    case LIST_COUNT: return AListCountCell;
    case LOGIN_AS: return AActionLoginAs;
    case MONEY: return AMoneyCell;
    case OPEN_MODAL_CELL: return AOpenModalCell;
    case PASSWORD_EXPIRY: return APasswordExpiryCell;
    case RECEIPT: return AReceiptCell;
    case SHELTER_STATUS: return AShelterStatusCell;
    case STAFF_EMAIL_VERIFICATION: return AStaffEmailVerificationCell;
    case STAFF_PHONE_VERIFICATION: return AStaffPhoneVerificationCell;
    case TAG: return ATagCell;
    case TEXT_AREA: return ATextArea;
    case VOUCHERS: return MActionVouchers;
  }
}

// EVENTS
function onGridContainerResize() {
  const overflow = gridContainer.value.scrollWidth - gridContainer.value.clientWidth > 1;
  if (hasOverflow.value !== overflow) hasOverflow.value = overflow;

  if (hasOverflow.value) setFrozenColumns();
  else resetFrozenColumns();
}

function onToggleColumns() {
  setFrozenColumns();
}

function onSelectRow() {
  selectAll.value = selectedRows.value.length === rows.value.length;
}

function onSelectAllRows() {
  if (!selectedRows.value.length) {
    resetSelectAll();
  }

  selectAll.value = selectedRows.value.length === rows.value.length;
}

function onNewPage(params: PageState) {
  page.value = params.page + 1;

  emits('onParamsUpdate');
}

function onEditCell(newValue: any, originalData: any, field: any) {
  emits('onFieldUpdate', newValue, originalData, field);

  // Clear edit mode after update
  cellOnEditMode.value = '';

  // NOTE: The code below resets the width of the edited column, allowing it to adapt dynamically
  // to the largest updated value, i.e., the longest status in Animal Status.
  frozenColumns.value = [];
  nextTick(() => {
    setFrozenColumns();
  });
}

function onCancelEdit() {
  cellOnEditMode.value = '';
}

// ORGANIZE THIS

function setNewEditCell(params: CellUniqueIdenfier) {
  cellOnEditMode.value = getCellUniqueIndetifier(params);
}

function isFieldOnEditMode(params: CellUniqueIdenfier) {
  return cellOnEditMode.value === getCellUniqueIndetifier(params);
}

function getCellUniqueIndetifier(params: CellUniqueIdenfier) {
  const { field, index, rowIndex } = params;

  return `${field}-col_${index}-row_${rowIndex}`;
}

function isListBoxEdit(settings: any, field: any, rowIndex: any, index: any) {
  const needsListbox = [DROPDOWN, MULTISELECT].includes(settings?.edit?.type);

  return needsListbox && isFieldOnEditMode({ field, rowIndex, index });
}

function isEditable(settings: any, field: any, editable: boolean) {
  if (!editable) return false;

  const hasNotEditableValues = settings?.edit?.notEditableValues;
  if (!hasNotEditableValues) return editable;

  // NOTE :: In the future when the internationalization is implemented, this should be changed to the translated values.
  const isNotEditable = hasNotEditableValues.includes(field);

  return editable && !isNotEditable;
}

function isOddRow(rowIndex: number | string) {
  const numericIndex = typeof rowIndex === 'number' ? rowIndex : Number(rowIndex);

  return Number.isFinite(numericIndex) && numericIndex % 2 > 0;
}

function toRowIndex(rowIndex: number | string) {
  return typeof rowIndex === 'number' ? rowIndex : Number(rowIndex);
}
</script>

<template>
  <div class="relative">
    <ALoadingState
      v-if="loading && !rows.length"
      :scrollHeight="customLoadingHeight || scrollHeight"
    />

    <div
      v-if="rows.length"
      ref="gridContainer"
      :class="classes"
      :style="{ 'max-height': customHeight || scrollHeight }"
      @scroll="cellOnEditMode ? scrollPosition++ : null"
    >
      <div
        v-for="({ header, field, sortable, editable, settings }, index) in visibleHeaders"
        :key="field"
        :class="[`${FIELD_CLASS}_${field} block`, { 'frozen sticky z-50': hasOverflow && getFrozenState(field) }]"
        :style="getFrozenColumnWidth(field, hasOverflow)"
      >
        <MHeaders
          v-model:selectedRows="selectedRows"
          :field
          :header
          :rows
          :settings
          :sortable
          @onSort="emits('onParamsUpdate')"
        />

        <div v-if="rows.length">
          <div
            v-for="(row, rowIndex) in rows"
            :key="rowIndex"
            :class="[
              'grid-item relative',
              {
                'odd-row': isOddRow(rowIndex),
                'editable-row cursor-pointer': isEditable(settings, row[field], editable),
              },
            ]"
          >
            <Checkbox
              v-if="field === 'rowSelector'"
              v-model="selectedRows"
              name="rowSelector1"
              :inputId="`cell_${field}_${index}`"
              :value="row"
              @change="onSelectRow"
            />

            <div v-else-if="field === 'asideColumn'">
              <slot
                name="asideButton"
                :props="row"
              />
            </div>

            <div v-else-if="field === 'tunnel'">
              <slot
                v-if="$slots.tunnel"
                name="tunnel"
                :props="row"
              />
            </div>

            <div v-else-if="field === 'actionsColumn'">
              <slot
                v-if="$slots.actions"
                name="actions"
                :props="row"
              />
            </div>

            <!-- TODO :: This may be a component (aka Row) -->
            <div v-else>
              <MEditCell
                v-if="isListBoxEdit(settings, field, rowIndex, index)"
                v-model:cellOnEditMode="cellOnEditMode"
                ref="clickOutsideTarget"
                :column="visibleHeaders[index]"
                :row
                :rowIndex="toRowIndex(rowIndex)"
                :scrollPosition
                :settings
                :type="settings?.edit?.type"
                @onFieldUpdate="onEditCell"
              />

              <AApplicationStatusDropdown
                v-else-if="
                  settings?.edit?.type === DROPDOWN_MODAL &&
                  isFieldOnEditMode({ field, rowIndex: toRowIndex(rowIndex), index })
                "
                :formType="settings?.edit?.formType"
                :isFormsAndApplications="settings?.edit?.isFormsAndApplications"
                :loading
                :rowInfo="row"
                @blur="cellOnEditMode = ''"
              />

              <div
                v-else
                :class="[
                  settings?.cellWidth || 'max-w-64',
                  settings?.component === IMAGE && !settings?.hideImageStatusOverlays
                    ? 'overflow-visible'
                    : 'overflow-hidden text-ellipsis whitespace-nowrap',
                ]"
                @click="
                  isEditable(settings, row[field], editable) &&
                  setNewEditCell({ field, rowIndex: toRowIndex(rowIndex), index })
                "
              >
                <!--
                  Opt-in custom cell slot. Renders ONLY when the parent provides a #cell slot AND
                  the column has no `settings.component`. Pages that don't provide a #cell slot or
                  configure a standard renderer keep the existing <component :is> path below.
                -->
                <slot
                  v-if="$slots.cell && !settings?.component"
                  name="cell"
                  :field
                  :props="row"
                  :rowIndex
                />
                <component
                  v-else
                  :field="row[field]"
                  :fieldName="field"
                  :is="componentSwitcher(settings?.component)"
                  :isEditMode="isFieldOnEditMode({ field, rowIndex: toRowIndex(rowIndex), index })"
                  :oddRow="isOddRow(rowIndex)"
                  :rowData="row"
                  :settings
                  @onCancelEdit="onCancelEdit"
                  @onFieldUpdate="onEditCell"
                  @onRefreshData="emits('onParamsUpdate')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!rows.length && !loading"
      :class="classes"
    >
      <div
        v-for="{ header, field, sortable, settings } in visibleHeaders"
        class="block w-full"
        :key="field"
      >
        <MHeaders
          :field
          :header
          :rows
          :settings
          :sortable
          @onSort="emits('onParamsUpdate')"
        />
      </div>
    </div>

    <AEmptyState
      v-if="!rows.length && !loading"
      :scrollHeight="customEmptyStateHeight || scrollHeight"
    />

    <MPaginator
      v-if="rows.length && hasPaginator"
      :instanceComposable
      :pageLinkSize
      :totalRecords
      @onNewPage="onNewPage($event)"
      @onRowsPerPageUpdate="emits('onParamsUpdate')"
    />
  </div>
</template>

<style scoped>
.grid-container {
  @apply grid border;
  grid-template-columns: v-bind(columnsGridSize);
}

.grid-item {
  @apply grid h-14 w-full items-center whitespace-nowrap border-b border-r bg-white p-2.5 text-sm;
}

.grid-container.compact .grid-item {
  @apply h-12 py-0;
}

.grid-container.large .grid-item {
  @apply h-20;
}

.odd-row {
  @apply bg-gray-100;
}
</style>
